/**
 * Create or edit an AI report schedule — run it ONCE at a future time, or on a
 * repeating cadence (daily and longer) at a chosen local time of day.
 *
 * Create mode is driven by an existing run (sourceRunId → the SQL/prompt are
 * copied server-side). Edit mode loads an existing schedule by scheduleId.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Select } from '@/components/ui/Select';
import { Spinner } from '@/components/ui/Spinner';
import { Alert } from '@/components/ui/Alert';
import {
  aiReportsApi,
  type CreateSchedulePayload,
  type ScheduleCadenceUnit,
  type UpdateSchedulePayload,
} from '@/services/aiReports.service';
import { getErrorMessage } from '@/services/apiClient';
import { invalidateMyReports } from '@/hooks/useMyReports';
import { DatePicker } from '@/components/ui/DatePicker';

const UNIT_OPTIONS: Array<{ value: ScheduleCadenceUnit; label: string }> = [
  { value: 'day', label: 'Day(s)' },
  { value: 'week', label: 'Week(s)' },
  { value: 'month', label: 'Month(s)' },
  { value: 'year', label: 'Year(s)' },
];

const todayLocal = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
};

export function AiReportScheduleModal({
  isOpen,
  onClose,
  sourceRunId,
  scheduleId,
  defaultTitle,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  /** Create a schedule from this existing run. */
  sourceRunId?: number;
  /** Edit an existing schedule (mutually exclusive with sourceRunId). */
  scheduleId?: number;
  defaultTitle?: string;
  onSaved?: (scheduleId: number) => void;
}) {
  const queryClient = useQueryClient();
  const isEdit = !!scheduleId;

  const [mode, setMode] = useState<'once' | 'recurring'>('recurring');
  const [title, setTitle] = useState('');
  const [runAt, setRunAt] = useState('');                 // datetime-local
  const [cadenceUnit, setCadenceUnit] = useState<ScheduleCadenceUnit>('week');
  const [cadenceCount, setCadenceCount] = useState(1);
  const [timeOfDay, setTimeOfDay] = useState('09:00');
  const [startDate, setStartDate] = useState(todayLocal());

  // Edit: load the existing schedule and seed the form.
  const existing = useQuery({
    queryKey: ['ai-report-schedule', scheduleId],
    queryFn: () => aiReportsApi.schedules.get(scheduleId as number),
    enabled: isOpen && isEdit,
  });

  useEffect(() => {
    if (!isOpen) return;
    if (isEdit && existing.data) {
      const s = existing.data;
      setMode(s.recurring ? 'recurring' : 'once');
      setTitle(s.title ?? '');
      setCadenceUnit((s.cadenceUnit ?? 'week') as ScheduleCadenceUnit);
      setCadenceCount(s.cadenceCount || 1);
      setTimeOfDay((s.timeOfDay ?? '09:00:00').slice(0, 5));
      // Re-anchor from the existing next run so editing (e.g. the title) doesn't
      // silently reset a weekly/monthly schedule to today.
      if (s.startDate) setStartDate(s.startDate);
      if (s.nextRunInput) setRunAt(s.nextRunInput);
    } else if (!isEdit) {
      setTitle(defaultTitle ?? '');
    }
  }, [isOpen, isEdit, existing.data, defaultTitle]);

  const save = useMutation({
    mutationFn: async () => {
      if (isEdit) {
        const payload: UpdateSchedulePayload = { title, mode };
        if (mode === 'once') {
          payload.runAt = runAt.replace('T', ' ');
        } else {
          payload.cadenceUnit = cadenceUnit;
          payload.cadenceCount = cadenceCount;
          payload.timeOfDay = timeOfDay;
          payload.startDate = startDate;
        }
        return aiReportsApi.schedules.update(scheduleId as number, payload);
      }
      const payload: CreateSchedulePayload = { sourceRunId, title, mode };
      if (mode === 'once') {
        payload.runAt = runAt.replace('T', ' ');
      } else {
        payload.cadenceUnit = cadenceUnit;
        payload.cadenceCount = cadenceCount;
        payload.timeOfDay = timeOfDay;
        payload.startDate = startDate;
      }
      return aiReportsApi.schedules.create(payload);
    },
    onSuccess: (s) => {
      invalidateMyReports(queryClient);
      queryClient.invalidateQueries({ queryKey: ['ai-report-schedule', s.id] });
      onSaved?.(s.id);
      onClose();
    },
  });

  const canSave = mode === 'once' ? runAt.trim() !== '' : cadenceCount >= 1;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={isEdit ? 'Edit schedule' : 'Schedule report'}
      size="md"
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>Cancel</Button>
          <Button onClick={() => save.mutate()} loading={save.isPending} disabled={!canSave}>
            {isEdit ? 'Save schedule' : 'Create schedule'}
          </Button>
        </>
      }
    >
      {isEdit && existing.isLoading ? (
        <div className="flex items-center gap-2 text-slate-500"><Spinner size="sm" /> Loading…</div>
      ) : (
        <div className="space-y-5">
          {save.isError && (
            <Alert variant="error">{getErrorMessage(save.error, 'Failed to save the schedule.')}</Alert>
          )}

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-700">Title (optional)</label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Weekly new-patient report"
              className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
            />
          </div>

          {/* Mode toggle */}
          <div className="flex gap-4">
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="radio" checked={mode === 'recurring'} onChange={() => setMode('recurring')} />
              Recurring
            </label>
            <label className="flex items-center gap-2 text-sm text-slate-700">
              <input type="radio" checked={mode === 'once'} onChange={() => setMode('once')} />
              Once (future)
            </label>
          </div>

          {mode === 'once' ? (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-700">Run at</label>
              <input
                type="datetime-local"
                value={runAt}
                onChange={(e) => setRunAt(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
              />
              <p className="mt-1 text-xs text-slate-500">Your local time.</p>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Every</label>
                  <input
                    type="number"
                    min={1}
                    max={365}
                    value={cadenceCount}
                    onChange={(e) => setCadenceCount(Math.max(1, Number(e.target.value) || 1))}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Unit</label>
                  <Select<ScheduleCadenceUnit>
                    options={UNIT_OPTIONS}
                    value={cadenceUnit}
                    onChange={(v) => setCadenceUnit((v ?? 'week') as ScheduleCadenceUnit)}
                  />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-sm font-medium text-slate-700">Time of day</label>
                  <input
                    type="time"
                    value={timeOfDay}
                    onChange={(e) => setTimeOfDay(e.target.value)}
                    className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm focus:border-slate-900 focus:outline-none"
                  />
                </div>
                <div>
                  <label htmlFor="ai-report-start-date" className="mb-1 block text-sm font-medium text-slate-700">Starting</label>
                  <DatePicker
                    id="ai-report-start-date"
                    value={startDate}
                    onChange={setStartDate}
                  />
                </div>
              </div>
              <p className="text-xs text-slate-500">
                Runs in your local time. If the first slot has already passed today, it starts at the
                next interval.
              </p>
            </div>
          )}
        </div>
      )}
    </Modal>
  );
}
